import { CheckIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { Button } from '../button/Button';
import { Card } from '../card/Card';
import { Checkbox } from '../checkbox/Checkbox';
import { Dialog } from '../dialog/components/Dialog';
import { Input } from '../form/input/Input';
import { Popover } from '../Popover/components/Popover';
import { Tag } from '../tag/Tag';
import { Text } from '../text';

export type TagOption = {
  id: string;
  name: string;
};

type Props = {
  /** マスタタグ一覧 */
  tags: TagOption[];
  /** 選択されたタグIDの配列 */
  selectedTagIds: string[];
  /** タグ選択変更時のコールバック */
  onSelectedTagIdsChange: (tagIds: string[]) => void;
  /** 検索クエリからの新規タグ作成 */
  onCreateTag?: (name: string) => void;
  /** タグの名称更新（true で成功、false で失敗。失敗時は編集モードを維持する） */
  onUpdateTag?: (tagId: string, name: string) => Promise<boolean>;
  /**
   * タグの削除。
   * 削除成功後の選択状態の更新は呼び出し元の責務。
   * （UI 側で勝手に `onSelectedTagIdsChange` を発火すると即時保存ラッパーで紐付け解除 API が誤発火するため）
   */
  onDeleteTag?: (tagId: string) => void;
  /** 管理者かどうか（CRUD 操作の表示制御） */
  isAdmin?: boolean;
  /** プレースホルダー */
  placeholder?: string;
  /** 無効化 */
  disabled?: boolean;
};

/**
 * タグ選択＆マスタ管理コンポーネント（Controlled 純UI）
 *
 * Notion 風のマルチセレクト UI + admin 向けインライン CRUD。
 * 業務ロジックは含まず、CRUD コールバックは呼び出し元で API と接続する。
 */
export const TagSelectWithCrud = ({
  tags,
  selectedTagIds,
  onSelectedTagIdsChange,
  onCreateTag,
  onUpdateTag,
  onDeleteTag,
  isAdmin = false,
  placeholder,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const canCreate = isAdmin && Boolean(onCreateTag);
  const canEdit = isAdmin && Boolean(onUpdateTag);
  const canDelete = isAdmin && Boolean(onDeleteTag);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TagOption | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));
  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const trimmedQuery = searchQuery.trim();
  const hasExactMatch = tags.some((tag) => tag.name.toLowerCase() === trimmedQuery.toLowerCase());
  const showCreateOption = canCreate && trimmedQuery.length > 0 && !hasExactMatch;
  const defaultPlaceholder = canCreate
    ? t('tag.placeholder.searchWithCreate')
    : t('tag.placeholder.search');

  useEffect(() => {
    if (editingTagId) editInputRef.current?.focus();
  }, [editingTagId]);

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onSelectedTagIdsChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onSelectedTagIdsChange([...selectedTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onSelectedTagIdsChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleCreateFromQuery = () => {
    if (!onCreateTag || trimmedQuery.length === 0) return;
    onCreateTag(trimmedQuery);
    setSearchQuery('');
  };

  const handleUpdateTag = async () => {
    if (!editingTagId || !onUpdateTag) return;
    const ok = await onUpdateTag(editingTagId, editingTagName);
    // 失敗時は編集モードを維持して再入力を許可
    if (!ok) return;
    setEditingTagId(null);
    setEditingTagName('');
  };

  const handleDeleteTag = () => {
    if (!deleteTarget || !onDeleteTag) return;
    onDeleteTag(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setEditingTagId(null);
      setEditingTagName('');
    }
  };

  const dropdownContent = (
    <Card className="flex w-100 flex-col overflow-hidden p-0">
      <div className="border-border-base border-b p-2">
        <div className="flex flex-wrap items-center gap-1">
          {selectedTags.map((tag) => (
            <Tag
              key={tag.id}
              size="small"
              onRemove={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag.id);
              }}
            >
              {tag.name}
            </Tag>
          ))}
          <Input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={selectedTags.length > 0 ? undefined : (placeholder ?? defaultPlaceholder)}
            className="min-w-20 flex-1 border-none p-0 shadow-none focus:ring-0"
            autoComplete={false}
          />
        </div>
      </div>

      <div className="flex max-h-60 flex-col gap-0.5 overflow-y-auto p-1">
        {filteredTags.length === 0 && !showCreateOption ? (
          <div className="px-3 py-2">
            <Text variant="description" className="text-text-description">
              {t('common.status.no-data')}
            </Text>
          </div>
        ) : (
          filteredTags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            const isEditing = editingTagId === tag.id;

            if (isEditing) {
              return (
                <div key={tag.id} className="flex items-center gap-1 px-2 py-1">
                  <Input
                    ref={editInputRef}
                    value={editingTagName}
                    onChange={(e) => setEditingTagName(e.target.value)}
                    onKeyDown={handleEditKeyDown}
                    className="h-7 flex-1"
                    maxLength={20}
                    autoComplete={false}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleUpdateTag}
                    aria-label={t('common.actions.save')}
                    className="h-7 w-7"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingTagId(null);
                      setEditingTagName('');
                    }}
                    aria-label={t('common.actions.cancel')}
                    className="h-7 w-7"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </Button>
                </div>
              );
            }

            return (
              <div
                key={tag.id}
                className={cn(
                  'group flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-neutral-100',
                  isSelected && 'bg-brand-100',
                )}
                onClick={() => handleToggleTag(tag.id)}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggleTag(tag.id);
                  }
                }}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
              >
                <Checkbox checked={isSelected} className="pointer-events-none shrink-0" />
                <Text truncate className="flex-1">
                  {tag.name}
                </Text>
                {(canEdit || canDelete) && (
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          setEditingTagId(tag.id);
                          setEditingTagName(tag.name);
                        }}
                        aria-label={t('tag.actions.edit')}
                        className="h-6 w-6"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          setDeleteTarget(tag);
                        }}
                        aria-label={t('tag.actions.delete')}
                        className="h-6 w-6"
                      >
                        <TrashIcon className="h-3.5 w-3.5 text-text-error" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {showCreateOption && (
          <div
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-neutral-100"
            onClick={handleCreateFromQuery}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCreateFromQuery();
              }
            }}
            role="option"
            aria-selected={false}
            tabIndex={0}
          >
            <Text variant="description">{t('tag.actions.createInline')}</Text>
            <Tag size="small">{trimmedQuery}</Tag>
          </div>
        )}
      </div>

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('tag.actions.delete')}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDeleteTag}
      >
        <Text>{t('tag.messages.deleteConfirm')}</Text>
      </Dialog>
    </Card>
  );

  return (
    <div className="flex flex-col gap-2">
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setSearchQuery('');
            setEditingTagId(null);
            setEditingTagName('');
          }
        }}
        contentClassName="p-0 shadow-none"
        content={dropdownContent}
        contentAlign="start"
        disabled={disabled}
      >
        <div
          className={cn(
            'input-field-without-height',
            'flex min-h-9 w-full cursor-pointer flex-wrap items-center gap-1 px-3 py-1.5',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {selectedTags.length > 0 ? (
            selectedTags.map((tag) => (
              <Tag
                key={tag.id}
                size="small"
                onRemove={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(tag.id);
                }}
              >
                {tag.name}
              </Tag>
            ))
          ) : (
            <span className="truncate text-sm text-text-muted">
              {placeholder ?? defaultPlaceholder}
            </span>
          )}
        </div>
      </Popover>
    </div>
  );
};
